import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../ui/tabs';
import {
  Server, Loader2, Download, Mail, Sparkles, Trash2, Plus, RefreshCw, FileText, CheckCircle2, Star, CreditCard, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../lib/formatUtils';

const BASE = process.env.REACT_APP_BACKEND_URL;
const AVAILABLE_TENURES = [1, 2, 3, 4, 5];
const DEFAULT_TENURES = [1, 2, 3];

const blankOption = (tenure_years) => ({
  tenure_years,
  // String state — '' renders as empty input (no stuck '0'). Coerced to
  // Number at compute/save time only.
  amount_per_year: '',
  discount_amount: '',
  is_recommended: false,
  // Per-row override flag. When false, the row's amount_per_year is
  // auto-derived from `form.base_amount_per_year`. When true, the row
  // is editable independently (e.g. SLA-tier bump in year 3).
  custom_price: false,
});

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const computeOption = (o, taxRate, baseAmountPerYear) => {
  // Effective per-year price for this row — base unless custom override.
  const effectivePerYear = o.custom_price
    ? _num(o.amount_per_year)
    : _num(baseAmountPerYear);
  const subtotal = effectivePerYear * (_num(o.tenure_years) || 1);
  const afterDisc = Math.max(0, subtotal - _num(o.discount_amount));
  const tax = (afterDisc * _num(taxRate)) / 100;
  return {
    effectivePerYear,
    subtotal,
    tax,
    total: afterDisc + tax,
  };
};

/**
 * Per-hosting-account custom quote dialog.
 *
 * The client's flow is: we share a multi-tenure quote (1y / 2y / 3y ...);
 * they pick a preferred tenure; we raise the proforma for that one.
 *
 * Two-tab layout:
 *  - "New Quote" — multi-tenure option builder with auto-suggest.
 *  - "Saved (N)" — list of past quotes with Download PDF / Email / Convert actions.
 */
export const HostingQuoteDialog = ({ open, onClose, hosting }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('new');
  const [rateCard, setRateCard] = useState(null);
  const [savedQuotes, setSavedQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState({});
  const [emailBusy, setEmailBusy] = useState({});
  const [convertBusy, setConvertBusy] = useState({});
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null, to: '', cc: '' });
  const [tenurePicker, setTenurePicker] = useState({ open: false, quote: null, selected: null });
  // Payment-link status per quote. Map<quote_id, {status, amount, paid_at, created_at, short_url, tenure_years}>.
  // Lets us render a "Link sent" / "Paid" chip on each saved quote row
  // so Ops can see at-a-glance which quotes have already been converted
  // to payment links without opening the link dialog.
  const [paylinksByQuote, setPaylinksByQuote] = useState({});

  const fresh = useMemo(() => ({
    plan_label: hosting?.plan || 'Custom Hosting',
    disk_space_gb: '',
    bandwidth_gb: '',
    num_emails: '',
    num_databases: '',
    // Single base per-year price. Each tenure row inherits this unless
    // it has its own custom_price override. Ops typically type it once.
    base_amount_per_year: '',
    tax_rate: 18,
    notes: '',
    valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    client_name: hosting?.client_name || '',
    client_email: hosting?.client_email || '',
    // tenure_options is the heart of the form — defaults to 3 rows.
    tenure_options: DEFAULT_TENURES.map((t, idx) => ({
      ...blankOption(t),
      is_recommended: idx === DEFAULT_TENURES.length - 1,
    })),
  }), [hosting]);
  const [form, setForm] = useState(fresh);

  // Empty-input safe `setField` — number-typed inputs store the raw
  // string ('' allowed) so the user can backspace. Coercion is deferred
  // to compute/save time. Non-number inputs behave as before.
  const setField = (k) => (e) => setForm(f => ({
    ...f,
    [k]: e?.target ? e.target.value : e,
  }));

  const updateOption = (idx, patch) => {
    setForm(f => ({
      ...f,
      tenure_options: f.tenure_options.map((o, i) => i === idx ? { ...o, ...patch } : o),
    }));
  };

  const toggleRecommended = (idx) => {
    setForm(f => ({
      ...f,
      tenure_options: f.tenure_options.map((o, i) => ({ ...o, is_recommended: i === idx })),
    }));
  };

  const addOption = () => {
    setForm(f => {
      const taken = new Set(f.tenure_options.map(o => o.tenure_years));
      const next = AVAILABLE_TENURES.find(t => !taken.has(t));
      if (!next) {
        toast.error('All tenure options (1-5 years) are already added.');
        return f;
      }
      return {
        ...f,
        tenure_options: [...f.tenure_options, blankOption(next)].sort((a, b) => a.tenure_years - b.tenure_years),
      };
    });
  };

  const removeOption = (idx) => {
    setForm(f => {
      if (f.tenure_options.length <= 1) {
        toast.error('At least one tenure option is required.');
        return f;
      }
      const next = f.tenure_options.filter((_, i) => i !== idx);
      // If we removed the recommended row, auto-promote the last one.
      if (!next.some(o => o.is_recommended) && next.length) {
        next[next.length - 1].is_recommended = true;
      }
      return { ...f, tenure_options: next };
    });
  };

  // Reset form whenever the dialog opens (or hosting changes).
  useEffect(() => { if (open) setForm(fresh); }, [open, fresh]);

  // Client name/email prefill fallback — older hosting rows may lack
  // these fields. We try (1) /clients/{hosting.client_id}, then
  // (2) /projects/{hosting.project_id} → /clients/{linked.client_id}.
  // Identical to the AMCQuoteDialog fallback so behaviour is parallel.
  useEffect(() => {
    if (!open) return;
    if (form.client_name && form.client_email) return;
    let cancelled = false;

    const applyClient = (c) => {
      if (cancelled || !c) return;
      setForm(f => ({
        ...f,
        client_name:  f.client_name  || c.name || c.company_name || '',
        client_email: f.client_email || c.email || '',
      }));
    };

    const run = async () => {
      // Path 1: direct client_id on the hosting row.
      if (hosting?.client_id) {
        try {
          const r = await api.get(`/clients/${hosting.client_id}`);
          if (r.data?.success) { applyClient(r.data.data); return; }
        } catch { /* fall through */ }
      }
      // Path 2: walk through the project.
      if (hosting?.project_id) {
        try {
          const r = await api.get(`/projects/${hosting.project_id}`);
          if (r.data?.success) {
            const p = r.data.data || {};
            if (p.client_id) {
              try {
                const cr = await api.get(`/clients/${p.client_id}`);
                if (cr.data?.success) applyClient(cr.data.data);
              } catch { /* ignore */ }
            }
          }
        } catch { /* non-fatal */ }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, hosting?.client_id, hosting?.project_id, form.client_name, form.client_email]);

  // Pull the rate card once per open so auto-suggest works without a
  // network blip when the user clicks "Auto-suggest".
  useEffect(() => {
    if (!open || rateCard) return;
    api.get('/hosting/quotes/rate-card').then(r => {
      if (r.data?.success) setRateCard(r.data.data);
    }).catch(() => { /* operator may not have settings.view; non-fatal */ });
  }, [open, rateCard]);

  const loadSavedQuotes = useCallback(async () => {
    if (!hosting?.id) return;
    setLoadingQuotes(true);
    try {
      const r = await api.get(`/hosting/quotes?hosting_id=${hosting.id}`);
      if (r.data?.success) setSavedQuotes(r.data.data || []);
    } finally {
      setLoadingQuotes(false);
    }
    // Pull payment-link status for ALL quotes attached to this hosting
    // in one round-trip. We then group by from_quote_id in memory. Done
    // in parallel after the saved-quotes load so the table renders fast.
    try {
      const r = await api.get('/payment-links', {
        params: { entity_type: 'hosting', entity_id: hosting.id },
      });
      const links = r.data?.data || [];
      // For each quote, pick the most relevant status:
      //   paid > created > anything else (cancelled/expired).
      // If multiple, prefer the newest of the winning tier.
      const RANK = { paid: 3, created: 2, expired: 1, cancelled: 0 };
      const grouped = {};
      for (const l of links) {
        const qid = l.from_quote_id;
        if (!qid) continue;
        const cur = grouped[qid];
        const curRank = cur ? (RANK[cur.status] ?? -1) : -1;
        const newRank = RANK[l.status] ?? -1;
        if (newRank > curRank ||
            (newRank === curRank && (l.paid_at || l.created_at) > (cur?.paid_at || cur?.created_at || ''))) {
          grouped[qid] = l;
        }
      }
      setPaylinksByQuote(grouped);
    } catch {
      // Razorpay misconfig or no permission — silently skip the chips.
      setPaylinksByQuote({});
    }
  }, [hosting?.id]);

  useEffect(() => { if (open) loadSavedQuotes(); }, [open, loadSavedQuotes]);

  // Server-driven auto-suggest — pulls fresh rate-card prices applied
  // against the spec, with tenure-discount ladder per row.
  const autoSuggestAll = async () => {
    try {
      const r = await api.post('/hosting/quotes/auto-suggest-options', {
        disk_space_gb: Number(form.disk_space_gb) || 0,
        bandwidth_gb: Number(form.bandwidth_gb) || 0,
        num_emails: Number(form.num_emails) || 0,
        num_databases: Number(form.num_databases) || 0,
        tenures: form.tenure_options.map(o => Number(o.tenure_years)),
      });
      const opts = r?.data?.data?.options || [];
      if (!opts.length) {
        toast.error('Auto-suggest returned no options — check rate-card under Settings.');
        return;
      }
      setForm(f => ({
        ...f,
        // Auto-suggest returns per-tenure amounts. Use the 1-year option
        // as the base price (so the new base+discount UX inherits it
        // for the other rows), then per-row amounts kick into 'custom'
        // mode only when they differ from the base — keeps the UI clean.
        base_amount_per_year: String(
          (opts.find(x => Number(x.tenure_years) === 1) || opts[0]).amount_per_year || 0,
        ),
        tenure_options: opts.map(o => {
          const amount = Number(o.amount_per_year) || 0;
          const baseAmount = Number(
            (opts.find(x => Number(x.tenure_years) === 1) || opts[0]).amount_per_year,
          ) || 0;
          const isCustom = amount !== baseAmount;
          return {
            tenure_years: Number(o.tenure_years),
            amount_per_year: isCustom ? String(amount) : '',
            discount_amount: String(Number(o.discount_amount) || 0),
            is_recommended: !!o.is_recommended,
            custom_price: isCustom,
          };
        }),
        tax_rate: opts[0]?.tax_rate ?? f.tax_rate,
      }));
      toast.success(`Suggested pricing for ${opts.length} tenure(s) — adjust as needed.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                  : detail?.message || e?.message || 'Failed to auto-suggest pricing.';
      toast.error(msg);
    }
  };

  // Derived totals per row, for the inline preview.
  const optionTotals = useMemo(
    () => form.tenure_options.map(o => computeOption(o, form.tax_rate, form.base_amount_per_year)),
    [form.tenure_options, form.tax_rate, form.base_amount_per_year],
  );

  const handleSave = async () => {
    if (!form.client_name?.trim()) { toast.error('Client name is required.'); return; }
    const baseAmount = _num(form.base_amount_per_year);
    // Each row's effective per-year is the override OR the base.
    const effectivePerYear = (o) => o.custom_price ? _num(o.amount_per_year) : baseAmount;
    if (baseAmount <= 0 && form.tenure_options.every(o => !o.custom_price || _num(o.amount_per_year) <= 0)) {
      toast.error('Set a base 1-year price (or a custom per-year on at least one row).');
      return;
    }
    const invalid = form.tenure_options.find(o => effectivePerYear(o) <= 0);
    if (invalid) {
      toast.error(`The ${invalid.tenure_years}-year option has no price. Either set the base price or click Custom and enter a per-year for that row.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Don't leak the UI-only fields to the backend.
        base_amount_per_year: undefined,
        hosting_id: hosting?.id,
        client_id: hosting?.client_id,
        // Numeric spec fields — backend expects numbers, not strings.
        disk_space_gb: _num(form.disk_space_gb),
        bandwidth_gb: _num(form.bandwidth_gb),
        num_emails: _num(form.num_emails),
        num_databases: _num(form.num_databases),
        tax_rate: _num(form.tax_rate),
        tenure_options: form.tenure_options.map(o => ({
          tenure_years: o.tenure_years,
          amount_per_year: effectivePerYear(o),
          discount_amount: _num(o.discount_amount),
          tax_rate: _num(form.tax_rate),
          is_recommended: !!o.is_recommended,
        })),
      };
      const r = await api.post('/hosting/quotes', payload);
      if (r.data?.success) {
        toast.success(`Quote ${r.data.data.quote_number} created with ${r.data.data.tenure_options.length} tenure(s).`);
        await loadSavedQuotes();
        setTab('saved');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (quote) => {
    setPdfBusy(b => ({ ...b, [quote.id]: true }));
    try {
      // Backend now streams PDF bytes directly (mirrors payslip flow).
      // No filesystem write on the server → no permission edge-cases.
      const r = await api.post(`/hosting/quotes/${quote.id}/pdf`, null, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Open in a new tab so the user can preview + use the native save dialog.
      const w = window.open(url, '_blank', 'noopener');
      // Revoke the blob URL after a short delay so the new tab has time
      // to load it (Chrome/Firefox both keep the resource alive for the
      // tab once it's been navigated to).
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      if (!w) {
        // Pop-up blocked — fall back to a direct download trigger.
        const a = document.createElement('a');
        a.href = url;
        a.download = `hosting-quote-${quote.quote_number || quote.id.slice(-8).toUpperCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      loadSavedQuotes();
    } catch (e) {
      // With responseType=blob, axios returns the error body as a blob too.
      let msg = 'Failed to generate PDF.';
      try {
        const txt = await (e.response?.data?.text ? e.response.data.text() : Promise.resolve(''));
        if (txt) {
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setPdfBusy(b => ({ ...b, [quote.id]: false }));
    }
  };

  const openEmailDialog = (quote) => {
    setEmailDialog({ open: true, quote, to: quote.client_email || '', cc: '' });
  };

  const sendEmail = async () => {
    const q = emailDialog.quote;
    if (!emailDialog.to.trim()) { toast.error('Recipient email is required.'); return; }
    setEmailBusy(b => ({ ...b, [q.id]: true }));
    try {
      const r = await api.post(`/hosting/quotes/${q.id}/email`, {
        to: emailDialog.to.trim(),
        cc: emailDialog.cc.split(',').map(s => s.trim()).filter(Boolean),
      });
      if (r.data?.success) {
        toast.success(`Quote emailed to ${r.data.data.sent_to}.`);
        setEmailDialog({ open: false, quote: null, to: '', cc: '' });
        loadSavedQuotes();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email.');
    } finally {
      setEmailBusy(b => ({ ...b, [q.id]: false }));
    }
  };

  const handleDelete = async (quote) => {
    if (!window.confirm(`Delete quote ${quote.quote_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/hosting/quotes/${quote.id}`);
      toast.success('Quote deleted.');
      loadSavedQuotes();
    } catch {
      toast.error('Delete failed.');
    }
  };

  // Convert flow — opens a picker if the quote has multiple tenures and
  // has not yet been converted. For already-converted quotes, simply
  // navigates to the linked invoice.
  const handleConvertClick = (quote) => {
    if (quote.converted_to_invoice_id) {
      doConvert(quote, null);
      return;
    }
    const opts = quote.tenure_options || [];
    if (opts.length > 1) {
      // Choice 3a — force a picker dialog.
      const recommended = opts.find(o => o.is_recommended) || opts[opts.length - 1];
      setTenurePicker({ open: true, quote, selected: recommended.tenure_years });
    } else {
      // Single-option or legacy quote — convert directly.
      doConvert(quote, opts[0]?.tenure_years ?? null);
    }
  };

  const doConvert = async (quote, selectedTenureYears) => {
    setConvertBusy(b => ({ ...b, [quote.id]: true }));
    try {
      const body = { project_id: hosting?.project_id || null };
      if (selectedTenureYears != null) body.selected_tenure_years = selectedTenureYears;
      const r = await api.post(`/hosting/quotes/${quote.id}/convert-to-invoice`, body);
      if (r.data?.success) {
        const inv = r.data.data.invoice;
        const wasExisting = r.data.data.already_converted;
        toast.success(
          wasExisting
            ? `Quote was already converted to ${inv.invoice_number}.`
            : `Proforma ${inv.invoice_number} created for ${selectedTenureYears}-year tenure.`,
        );
        await loadSavedQuotes();
        setTenurePicker({ open: false, quote: null, selected: null });
        onClose?.();
        navigate(`/invoices?search=${encodeURIComponent(inv.invoice_number)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                  || e.response?.data?.detail
                  || 'Failed to create invoice.');
    } finally {
      setConvertBusy(b => ({ ...b, [quote.id]: false }));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="hosting-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              Custom Hosting Quote
              {hosting?.provider && (
                <Badge variant="outline" className="ml-2 text-xs">{hosting.provider}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Share a multi-tenure quote (1–5 years). The client picks their preferred tenure; you raise the proforma for the chosen one.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="new" data-testid="quote-tab-new">New Quote</TabsTrigger>
              <TabsTrigger value="saved" data-testid="quote-tab-saved">
                Saved ({savedQuotes.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="space-y-4 pt-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Client name" value={form.client_name} onChange={setField('client_name')} testId="quote-client-name" />
                <Field label="Client email" value={form.client_email} onChange={setField('client_email')} testId="quote-client-email" type="email" />
                <Field label="Plan label" value={form.plan_label} onChange={setField('plan_label')} testId="quote-plan-label" />
                <Field label="Valid until" type="date" value={form.valid_until} onChange={setField('valid_until')} testId="quote-valid-until" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Field label="Disk space (GB)" type="number" value={form.disk_space_gb} onChange={setField('disk_space_gb')} testId="quote-disk" />
                <Field label="Bandwidth (GB)" type="number" value={form.bandwidth_gb} onChange={setField('bandwidth_gb')} testId="quote-bandwidth" />
                <Field label="Emails" type="number" value={form.num_emails} onChange={setField('num_emails')} testId="quote-emails" />
                <Field label="Databases" type="number" value={form.num_databases} onChange={setField('num_databases')} testId="quote-dbs" />
              </div>

              {/* Tenure options builder */}
              <div className="rounded-md border bg-muted/20 p-3 space-y-3" data-testid="tenure-options-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h4 className="text-sm font-semibold">Tenure options</h4>
                    <p className="text-[11px] text-muted-foreground">
                      Type the base 1-year price once. Each tenure inherits it; only the discount changes per row.
                      Need to set a different per-year price for one tenure? Click &ldquo;Custom&rdquo; on that row.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={autoSuggestAll} data-testid="quote-autosuggest">
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      Auto-suggest all
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={addOption} data-testid="quote-add-tenure"
                            disabled={form.tenure_options.length >= AVAILABLE_TENURES.length}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Add tenure
                    </Button>
                  </div>
                </div>

                {/* Single base-price input — drives every non-custom row */}
                <div className="flex items-center gap-3 p-2.5 rounded-md border border-primary/30 bg-primary/5">
                  <Label className="text-xs font-medium whitespace-nowrap">Base 1-year price (₹)</Label>
                  <Input
                    type="number" min="0"
                    value={form.base_amount_per_year}
                    onChange={setField('base_amount_per_year')}
                    onFocus={(e) => e.target.select()}
                    placeholder="e.g. 6500"
                    className="h-8 max-w-[160px] text-sm font-semibold"
                    data-testid="quote-base-amount"
                  />
                  <p className="text-[10px] text-muted-foreground hidden sm:block">
                    Applied to every row below unless that row is set to Custom.
                  </p>
                </div>

                <div className="space-y-2">
                  {/* Column headers (hidden on mobile, shown on sm+) */}
                  <div className="hidden sm:grid sm:grid-cols-[80px_140px_1fr_140px_56px_36px] gap-2 px-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                    <span>Tenure</span>
                    <span>Per year (₹)</span>
                    <span>Discount (₹)</span>
                    <span className="text-right">Total (₹)</span>
                    <span className="text-center">Best</span>
                    <span></span>
                  </div>
                  {form.tenure_options.map((o, idx) => {
                    const t = optionTotals[idx];
                    const effectivePerYearShown = o.custom_price
                      ? o.amount_per_year
                      : (form.base_amount_per_year || '');
                    return (
                      <div key={`opt-${o.tenure_years}-${idx}`}
                           className={`grid grid-cols-2 sm:grid-cols-[80px_140px_1fr_140px_56px_36px] gap-2 items-center p-2 rounded-md border ${o.is_recommended ? 'bg-emerald-500/5 border-emerald-500/40' : 'bg-background border-border'}`}
                           data-testid={`tenure-row-${o.tenure_years}`}>
                        <div className="text-sm font-medium">
                          {o.tenure_years} {o.tenure_years === 1 ? 'year' : 'years'}
                        </div>
                        {/* Per-year cell — read-only by default, editable when 'Custom' is on */}
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number" min="0"
                            value={effectivePerYearShown}
                            disabled={!o.custom_price}
                            onChange={(e) => updateOption(idx, { amount_per_year: e.target.value })}
                            onFocus={(e) => e.target.select()}
                            data-testid={`tenure-${o.tenure_years}-amount`}
                            placeholder={o.custom_price ? 'e.g. 7500' : 'inherits base'}
                            className={o.custom_price ? '' : 'opacity-60 cursor-not-allowed'}
                          />
                          <button
                            type="button"
                            onClick={() => updateOption(idx, {
                              custom_price: !o.custom_price,
                              // When turning custom ON for the first time, seed the
                              // editable amount with the current base so the user
                              // doesn't lose context.
                              ...(o.custom_price ? {} : {
                                amount_per_year: o.amount_per_year || form.base_amount_per_year || '',
                              }),
                            })}
                            title={o.custom_price ? 'Reset to base price' : 'Set a different per-year for this tenure'}
                            data-testid={`tenure-${o.tenure_years}-custom-toggle`}
                            className={`text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap transition-colors ${o.custom_price
                              ? 'bg-amber-500/15 text-amber-700 border-amber-500/40 hover:bg-amber-500/25'
                              : 'text-muted-foreground border-border hover:bg-muted'}`}
                          >
                            {o.custom_price ? '↩︎ Reset' : 'Custom'}
                          </button>
                        </div>
                        <Input
                          type="number" min="0"
                          value={o.discount_amount}
                          onChange={(e) => updateOption(idx, { discount_amount: e.target.value })}
                          onFocus={(e) => e.target.select()}
                          data-testid={`tenure-${o.tenure_years}-discount`}
                          placeholder="0"
                        />
                        <div className="text-right text-sm font-semibold" data-testid={`tenure-${o.tenure_years}-total`}>
                          ₹{t.total.toFixed(0)}
                          {t.effectivePerYear > 0 && (
                            <p className="text-[10px] font-normal text-muted-foreground">
                              ₹{(t.total / o.tenure_years).toFixed(0)}/yr effective
                            </p>
                          )}
                        </div>
                        <button type="button" onClick={() => toggleRecommended(idx)}
                                className="flex justify-center"
                                title={o.is_recommended ? 'Recommended (BEST VALUE on PDF)' : 'Mark as recommended'}
                                data-testid={`tenure-${o.tenure_years}-star`}>
                          <Star className={`h-4 w-4 transition-colors ${o.is_recommended ? 'text-emerald-500 fill-emerald-500' : 'text-muted-foreground hover:text-emerald-500/70'}`} />
                        </button>
                        <button type="button" onClick={() => removeOption(idx)}
                                className="flex justify-center text-destructive/70 hover:text-destructive"
                                title="Remove this tenure"
                                data-testid={`tenure-${o.tenure_years}-remove`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Tax rate (%)</Label>
                    <Input type="number" min="0" value={form.tax_rate} onChange={setField('tax_rate')}
                           onFocus={(e) => e.target.select()}
                           data-testid="quote-tax" className="w-20 h-8 text-xs" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Applied to every tenure. Set 0 if your pricing is already tax-inclusive.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Notes (appear on the PDF)</Label>
                <Textarea rows={3} value={form.notes} onChange={setField('notes')} data-testid="quote-notes"
                          placeholder="Any custom inclusions, exclusions, or context for the client..." />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="quote-save-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                          : <><Plus className="h-4 w-4 mr-2" />Save Quote</>}
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="saved" className="pt-3">
              {loadingQuotes ? (
                <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
              ) : savedQuotes.length === 0 ? (
                <p className="text-sm text-center text-muted-foreground py-10">No quotes saved yet for this account.</p>
              ) : (
                <div className="space-y-2">
                  {savedQuotes.map(q => {
                    const opts = q.tenure_options || [];
                    const isMulti = opts.length > 1;
                    return (
                      <div key={q.id} className="rounded-md border p-3 space-y-2" data-testid={`saved-quote-${q.id}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-[200px]">
                            <div className="text-sm font-medium flex items-center gap-1.5">
                              {q.quote_number}
                              {q.converted_to_invoice_number && (
                                <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-500" data-testid={`quote-invoice-link-${q.id}`}>
                                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                                  {q.converted_to_invoice_number}
                                  {q.selected_tenure_years && <span className="ml-1">· {q.selected_tenure_years}y</span>}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              {q.plan_label || 'Custom Hosting'} · {q.client_name || '—'}
                              {q.sent_at && <> · <span className="text-emerald-500">Sent {formatDate(q.sent_at)}</span></>}
                            </p>
                            <PaylinkStatusChip link={paylinksByQuote[q.id]} testIdQuoteId={q.id} />
                          </div>
                          <Badge variant={q.status === 'accepted' ? 'default' : q.status === 'sent' ? 'secondary' : 'outline'} className="text-[10px] capitalize">{q.status}</Badge>
                          <Button size="sm" variant="outline" onClick={() => handleDownload(q)} disabled={pdfBusy[q.id]} data-testid={`quote-pdf-${q.id}`}>
                            {pdfBusy[q.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            <span className="ml-1.5">PDF</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEmailDialog(q)} disabled={emailBusy[q.id]} data-testid={`quote-email-${q.id}`}>
                            <Mail className="h-3.5 w-3.5" /><span className="ml-1.5">Email</span>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleConvertClick(q)}
                                  disabled={convertBusy[q.id]}
                                  title={q.converted_to_invoice_id ? 'Open the linked proforma invoice' : 'Convert to proforma invoice'}
                                  data-testid={`quote-convert-${q.id}`}>
                            {convertBusy[q.id]
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <FileText className="h-3.5 w-3.5" />}
                            <span className="ml-1.5">{q.converted_to_invoice_id ? 'View Invoice' : 'To Invoice'}</span>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(q)} className="text-destructive hover:text-destructive" data-testid={`quote-delete-${q.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {opts.length > 0 && (
                          <div className="flex gap-1.5 flex-wrap pt-1">
                            {opts.map(o => (
                              <Badge key={o.tenure_years}
                                     variant={o.is_recommended ? 'default' : 'outline'}
                                     className={`text-[10px] font-medium ${o.is_recommended ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20' : ''}`}
                                     data-testid={`quote-${q.id}-tenure-${o.tenure_years}`}>
                                {o.is_recommended && <Star className="h-2.5 w-2.5 mr-1 fill-current" />}
                                {o.tenure_years}y · ₹{Number(o.total_amount || 0).toFixed(0)}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {isMulti && q.selected_tenure_years && (
                          <p className="text-[10px] text-emerald-500 pt-1">
                            ✓ Client picked the {q.selected_tenure_years}-year option.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-2">
                    <Button size="sm" variant="ghost" onClick={loadSavedQuotes} data-testid="quote-refresh">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Tenure picker — shown when converting a multi-tenure quote */}
      <Dialog open={tenurePicker.open}
              onOpenChange={(o) => { if (!o) setTenurePicker({ open: false, quote: null, selected: null }); }}>
        <DialogContent className="sm:max-w-md" data-testid="tenure-picker-dialog">
          <DialogHeader>
            <DialogTitle>Which tenure did the client choose?</DialogTitle>
            <DialogDescription>
              Pick the tenure the client confirmed. The proforma will be raised for that amount.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(tenurePicker.quote?.tenure_options || []).map(o => (
              <button key={o.tenure_years} type="button"
                      onClick={() => setTenurePicker(s => ({ ...s, selected: o.tenure_years }))}
                      data-testid={`picker-tenure-${o.tenure_years}`}
                      className={`w-full flex items-center justify-between p-3 rounded-md border transition-colors text-left ${tenurePicker.selected === o.tenure_years
                        ? 'bg-emerald-500/10 border-emerald-500/60'
                        : 'bg-background border-border hover:bg-muted/50'}`}>
                <div>
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    {o.tenure_years} {o.tenure_years === 1 ? 'year' : 'years'}
                    {o.is_recommended && (
                      <Badge className="text-[9px] bg-emerald-500/20 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20">
                        <Star className="h-2.5 w-2.5 mr-1 fill-current" /> BEST
                      </Badge>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    ₹{Number(o.amount_per_year || 0).toFixed(0)}/yr
                    {o.discount_amount > 0 && <> · saves ₹{Number(o.discount_amount).toFixed(0)}</>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold">₹{Number(o.total_amount || 0).toFixed(0)}</p>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTenurePicker({ open: false, quote: null, selected: null })}>Cancel</Button>
            <Button onClick={() => doConvert(tenurePicker.quote, tenurePicker.selected)}
                    disabled={tenurePicker.selected == null || convertBusy[tenurePicker.quote?.id]}
                    data-testid="picker-confirm">
              {convertBusy[tenurePicker.quote?.id]
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
                : <><FileText className="h-4 w-4 mr-2" />Create Proforma</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nested email dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => { if (!o) setEmailDialog({ open: false, quote: null, to: '', cc: '' }); }}>
        <DialogContent className="sm:max-w-md" data-testid="quote-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Quote {emailDialog.quote?.quote_number}</DialogTitle>
            <DialogDescription>The PDF will be attached automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="To" value={emailDialog.to} onChange={(e) => setEmailDialog(s => ({ ...s, to: e.target.value }))} testId="quote-email-to" />
            <Field label="Cc (comma separated)" value={emailDialog.cc} onChange={(e) => setEmailDialog(s => ({ ...s, cc: e.target.value }))} testId="quote-email-cc" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, quote: null, to: '', cc: '' })}>Cancel</Button>
            <Button onClick={sendEmail} data-testid="quote-email-send"
                    disabled={emailDialog.quote && emailBusy[emailDialog.quote?.id]}>
              {emailDialog.quote && emailBusy[emailDialog.quote?.id]
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                : <><Mail className="h-4 w-4 mr-2" />Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value} onChange={onChange} data-testid={testId} />
  </div>
);


/**
 * Small per-row chip showing the latest payment-link status linked to
 * the quote (via `from_quote_id`). Renders nothing when no link exists.
 *
 *   paid     → "✅ Paid · ₹X,XXX via <method> · <date>"   (emerald, prominent)
 *   created  → "💳 Link sent · ₹X,XXX · <date>"          (amber/awaiting)
 *   else     → null (cancelled/expired don't add value here)
 */
function PaylinkStatusChip({ link, testIdQuoteId }) {
  if (!link) return null;
  const amt = Number(link.amount || 0).toLocaleString('en-IN');
  const dateIso = link.paid_at || link.created_at || '';
  const datePretty = dateIso
    ? new Date(dateIso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    : '';
  if (link.status === 'paid') {
    return (
      <span
        className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 font-medium"
        data-testid={`paylink-chip-paid-${testIdQuoteId}`}
        title={`Paid via Razorpay${link.razorpay_payment_id ? ' · ' + link.razorpay_payment_id : ''}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        Paid · ₹{amt}
        {datePretty && <span className="text-emerald-600/80 font-normal">· {datePretty}</span>}
      </span>
    );
  }
  if (link.status === 'created') {
    return (
      <a
        href={link.short_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-700 border border-amber-500/30 font-medium hover:bg-amber-500/15 transition-colors"
        data-testid={`paylink-chip-sent-${testIdQuoteId}`}
        title="Open the payment link in a new tab"
      >
        <CreditCard className="h-3 w-3" />
        Link sent · ₹{amt}
        {datePretty && <span className="text-amber-600/80 font-normal">· {datePretty}</span>}
        <Clock className="h-2.5 w-2.5 opacity-60" />
      </a>
    );
  }
  return null;
}
